import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import { LoginOutlined } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { LoginCredentials, authService } from '../services/authService';
import { safeConsoleError } from '../utils/safeConsole';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [formData, setFormData] = useState<LoginCredentials>({
    email: '',
    password: '',
  });
  
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetCodeSent, setResetCodeSent] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const safeLoginError = 'No se pudo iniciar sesión. Revisa las credenciales o usa restablecer contraseña.';
  const safeResetRequestError = 'No se pudo iniciar el restablecimiento. Verifica el correo e inténtalo de nuevo.';
  const safeResetConfirmError = 'No se pudo confirmar el restablecimiento. Verifica el código e inténtalo de nuevo.';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.email || !formData.password) {
      setError('Por favor, completa todos los campos');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      await login(formData);
      navigate('/');
    } catch (err) {
      safeConsoleError('Login error', 'auth.login.submit');
      setError(safeLoginError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestPasswordReset = async () => {
    const email = formData.email.trim().toLowerCase();
    if (!email) {
      setError('Introduce tu correo electrónico para restablecer la contraseña');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      setSuccess('');
      await authService.requestPasswordReset(email);
      setResetMode(true);
      setResetCodeSent(true);
      setSuccess('Te hemos enviado un código de verificación al correo indicado.');
    } catch (err) {
      safeConsoleError('Password reset request error', 'auth.password_reset.request');
      setError(safeResetRequestError);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmPasswordReset = async () => {
    const email = formData.email.trim().toLowerCase();
    if (!email || !resetCode.trim() || !newPassword) {
      setError('Completa correo, código de verificación y nueva contraseña');
      return;
    }

    try {
      setIsSubmitting(true);
      setError('');
      setSuccess('');
      await authService.confirmPasswordReset({
        email,
        code: resetCode.trim(),
        new_password: newPassword,
      });
      setResetMode(false);
      setResetCodeSent(false);
      setResetCode('');
      setNewPassword('');
      setSuccess('Contraseña actualizada. Ya puedes iniciar sesión.');
    } catch (err) {
      safeConsoleError('Password reset confirmation error', 'auth.password_reset.confirm');
      setError(safeResetConfirmError);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <LoginOutlined sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
          
          <Typography component="h1" variant="h4" gutterBottom>
            Iniciar Sesión
          </Typography>

          {error && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ width: '100%', mb: 2 }}>
              {success}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Correo Electrónico"
              name="email"
              autoComplete="email"
              autoFocus
              value={formData.email}
              onChange={handleChange}
              disabled={isSubmitting}
              type="email"
            />
            
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Contraseña"
              type="password"
              id="password"
              autoComplete="current-password"
              value={formData.password}
              onChange={handleChange}
              disabled={isSubmitting}
            />

            {resetMode && resetCodeSent && (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="resetCode"
                  label="Código de verificación"
                  name="resetCode"
                  autoComplete="one-time-code"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value)}
                  disabled={isSubmitting}
                />

                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="newPassword"
                  label="Nueva contraseña"
                  name="newPassword"
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={isSubmitting}
                />
              </>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 1 }}
              disabled={isSubmitting || resetMode}
            >
              {isSubmitting && !resetMode ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </Button>

            {!resetMode && (
              <Button
                type="button"
                fullWidth
                variant="text"
                sx={{ mb: 1 }}
                disabled={isSubmitting}
                onClick={handleRequestPasswordReset}
              >
                Restablecer contraseña
              </Button>
            )}

            {resetMode && resetCodeSent && (
              <Button
                type="button"
                fullWidth
                variant="contained"
                color="secondary"
                sx={{ mt: 1, mb: 1 }}
                disabled={isSubmitting}
                onClick={handleConfirmPasswordReset}
              >
                {isSubmitting ? 'Guardando...' : 'Guardar nueva contraseña'}
              </Button>
            )}

            {resetMode && (
              <Button
                type="button"
                fullWidth
                variant="text"
                sx={{ mb: 2 }}
                disabled={isSubmitting}
                onClick={() => {
                  setResetMode(false);
                  setResetCodeSent(false);
                  setResetCode('');
                  setNewPassword('');
                  setError('');
                }}
              >
                Volver al login
              </Button>
            )}

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2">
                ¿No tienes una cuenta?{' '}
                <Link to="/register">
                  Regístrate aquí
                </Link>
              </Typography>
            </Box>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default Login; 